import { useEffect, useMemo, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Center,
  CloseButton,
  Flex,
  Heading,
  HStack,
  Input,
  List,
  ListIcon,
  ListItem,
  Modal,
  ModalBody,
  ModalContent,
  ModalOverlay,
  SimpleGrid,
  Stack,
  Table,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { ExternalLinkIcon } from '@chakra-ui/icons';
import { CheckCircleIcon } from '@chakra-ui/icons';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { pricingDisclaimer } from '../../data/comparePages';
import { buildBannerPlaceholderUrl } from '../../utils/bannerImage';

const calculatorPresets = [
  { label: 'Small nonprofit', monthlyVolume: 5000, averageGift: 50, helper: '$5k/month' },
  { label: 'Growing nonprofit', monthlyVolume: 25000, averageGift: 75, helper: '$25k/month' },
  { label: 'Large nonprofit', monthlyVolume: 100000, averageGift: 100, helper: '$100k/month' },
];

const navItems = [
  { id: 'overview', label: 'Overview' },
  { id: 'cost-savings', label: 'Cost & savings' },
  { id: 'best-fit', label: 'Best fit' },
  { id: 'features', label: 'Features' },
  { id: 'why-purelygive', label: 'Why PurelyGive' },
  { id: 'faq', label: 'FAQ' },
];

const emphasizedRows = new Set([
  'Platform fee',
  'Subscription',
  'Processing fees',
  'Net received per $100 (example)',
]);

const sectionAnchorStyle = {
  scrollMarginTop: '110px',
};

const overviewVideoUrl = 'https://www.youtube.com/embed/Xfjr5WIs-mI?autoplay=1&rel=0';

const ComparisonPageTemplate = ({ data, siblingComparisons = [] }) => {
  const [monthlyVolume, setMonthlyVolume] = useState(25000);
  const [averageGift, setAverageGift] = useState(75);
  const [activeSection, setActiveSection] = useState('overview');
  const toast = useToast();
  const navContainerRef = useRef(null);
  const { isOpen: isVideoOpen, onOpen: onVideoOpen, onClose: onVideoClose } = useDisclosure();

  const calculator = useMemo(() => {
    const validVolume = Math.max(monthlyVolume, 0);
    const validAverageGift = Math.max(averageGift, 1);
    const estimatedMonthlyGifts = validVolume / validAverageGift;
    const totalAnnual = validVolume * 12;
    const annualCompetitorFees = totalAnnual * data.competitorAnnualFeeRate;
    const annualPurelyGiveFees = 0;

    return {
      estimatedMonthlyGifts,
      totalAnnual,
      annualCompetitorFees,
      annualPurelyGiveFees,
      annualSaved: annualCompetitorFees - annualPurelyGiveFees,
    };
  }, [monthlyVolume, averageGift, data.competitorAnnualFeeRate]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((entry) => entry.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio);

        if (visible[0]?.target?.id) {
          setActiveSection(visible[0].target.id);
        }
      },
      {
        rootMargin: '-35% 0px -55% 0px',
        threshold: [0.1, 0.25, 0.5],
      },
    );

    navItems.forEach((item) => {
      const section = document.getElementById(item.id);
      if (section) observer.observe(section);
    });

    return () => observer.disconnect();
  }, []);

  const faqSchema = {
    '@context': 'https://schema.org',
    '@type': 'FAQPage',
    mainEntity: data.faq.map((item) => ({
      '@type': 'Question',
      name: item.question,
      acceptedAnswer: { '@type': 'Answer', text: item.answer },
    })),
  };

  const productSchema = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: `PurelyGive vs ${data.competitor}`,
    description: data.description,
    brand: { '@type': 'Brand', name: 'PurelyGive' },
    category: 'Donation Platform Comparison',
  };

  const shareTitle = data.shareTitle || `PurelyGive vs ${data.competitor}`;
  const shareDescription = data.shareDescription || data.description;
  const shareUrl = `https://${window.location.host}/compare/${data.slug}`;
  const shareImage = buildBannerPlaceholderUrl(data.shareImageText || shareTitle);

  const copyComparisonLink = async () => {
    try {
      await navigator.clipboard.writeText(shareUrl);
      toast({ title: 'Comparison link copied', status: 'success', duration: 2200, isClosable: true });
    } catch {
      toast({ title: 'Unable to copy link', status: 'error', duration: 2200, isClosable: true });
    }
  };

  return (
    <Box bg="#f5f3ee" color="#0f393b" minH="100vh">
      <Helmet>
        <title>{data.title}</title>
        <meta name="description" content={shareDescription} />
        <link rel="canonical" href={shareUrl} />
        <meta property="og:type" content="website" />
        <meta property="og:site_name" content="PurelyGive" />
        <meta property="og:title" content={shareTitle} />
        <meta property="og:description" content={shareDescription} />
        <meta property="og:image" content={shareImage} />
        <meta property="og:url" content={shareUrl} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={shareTitle} />
        <meta name="twitter:description" content={shareDescription} />
        <meta name="twitter:image" content={shareImage} />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(productSchema)}</script>
      </Helmet>

      <Center py={{ base: 8, md: 12 }} px={{ base: 4, md: 6 }} bg="white">
        <Stack spacing={4} maxW="1100px" w="full">
          <Badge colorScheme="orange" alignSelf="flex-start">Donation platform comparison</Badge>
          <Heading as="h1" size={{ base: 'xl', md: '2xl' }}>PurelyGive vs {data.competitor}</Heading>
          <Text fontSize={{ base: 'md', md: 'lg' }} color="gray.600">
            PurelyGive helps nonprofits keep more of every donation by using a donor-supported model with no platform subscription fees.
          </Text>
          <List spacing={1} color="gray.700">
            <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />Nonprofits can receive the full donation amount through PurelyGive&rsquo;s donor-supported model.</ListItem>
            <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />No platform subscription fees for your organization.</ListItem>
            <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />Donors can optionally cover transaction costs.</ListItem>
            <ListItem><ListIcon as={CheckCircleIcon} color="green.500" />Compare total cost, not just features.</ListItem>
          </List>
          <Stack direction={{ base: 'column', sm: 'row' }} spacing={3} align={{ base: 'stretch', sm: 'center' }}>
            <Button colorScheme="orange" as="a" href="#cost-savings" w={{ base: 'full', sm: 'auto' }}>Estimate fees</Button>
            <Button variant="outline" as="a" href="#overview" w={{ base: 'full', sm: 'auto' }}>See overview</Button>
            <Button variant="ghost" onClick={copyComparisonLink} w={{ base: 'full', sm: 'auto' }}>Copy link to this comparison</Button>
          </Stack>
          <Text fontSize="xs" color="gray.500">Last updated: March 2026</Text>
        </Stack>
      </Center>

      <Box position="sticky" top="0" zIndex="sticky" bg="#f5f3ee" borderBottom="1px solid" borderColor="gray.200">
        <Center px={{ base: 0, md: 6 }} py={3} position="relative">
          <Box position="absolute" left="0" top="0" bottom="0" w="20" bgGradient="linear(to-r, #f5f3ee, transparent)" pointerEvents="none" display={{ base: 'block', md: 'none' }} />
          <Box position="absolute" right="0" top="0" bottom="0" w="20" bgGradient="linear(to-l, #f5f3ee, transparent)" pointerEvents="none" display={{ base: 'block', md: 'none' }} />
          <Flex
            ref={navContainerRef}
            maxW="1100px"
            w="full"
            gap={2}
            overflowX={{ base: 'auto', md: 'visible' }}
            px={{ base: 4, md: 0 }}
            css={{ scrollbarWidth: 'none' }}
          >
            {navItems.map((item) => (
              <Button
                key={item.id}
                size="sm"
                as="a"
                href={`#${item.id}`}
                variant={activeSection === item.id ? 'solid' : 'ghost'}
                colorScheme={activeSection === item.id ? 'orange' : 'gray'}
                flexShrink={0}
              >
                {item.label}
              </Button>
            ))}
          </Flex>
        </Center>
      </Box>

      <Box id="overview" style={sectionAnchorStyle} bg="#f5f3ee" py={10} px={{ base: 4, md: 6 }}>
        <Center>
          <Stack maxW="1100px" w="full" spacing={5}>
            <Heading size="md">Overview</Heading>
            <Text color="gray.700">{data.competitorOverview}</Text>
            <Text color="gray.700">Many nonprofits searching for a {data.keywordPhrase} are looking for predictable costs and better net donation retention.</Text>
            <Box bg="white" borderRadius="2xl" p={{ base: 4, md: 6 }} boxShadow="sm">
              <Heading size="md" mb={4}>Quick comparison</Heading>
              <Box w="full">
                <Table variant="simple" size="sm" sx={{
                  tableLayout: 'fixed',
                  'th, td': {
                    whiteSpace: 'normal',
                    wordBreak: 'break-word',
                    px: { base: 2, md: 4 },
                  },
                }}>
                  <Thead>
                    <Tr>
                      <Th>Feature</Th>
                      <Th>PurelyGive</Th>
                      <Th>{data.competitor}</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {data.quickComparison.map((row) => (
                      <Tr key={row.feature} fontWeight={emphasizedRows.has(row.feature) ? 'bold' : 'normal'}>
                        <Td fontWeight={emphasizedRows.has(row.feature) ? 'semibold' : 'medium'}>{row.feature}</Td>
                        <Td>{row.purelygive}</Td>
                        <Td>{row.competitor}</Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </Box>
            </Box>
            {data.bestFitOneLiner && (
              <Text color="gray.700" fontStyle="italic">{data.bestFitOneLiner}</Text>
            )}
          </Stack>
        </Center>
      </Box>

      <Box id="cost-savings" style={sectionAnchorStyle} bg="#f0eee7" py={10} px={{ base: 4, md: 6 }}>
        <Center>
          <Stack maxW="1100px" w="full" spacing={6}>
            <Heading size="md">Cost and savings</Heading>
            <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={6}>
              <Box bg="white" borderRadius="2xl" p={{ base: 4, md: 6 }} boxShadow="sm">
                <Heading size="sm" mb={4}>$100 donation example</Heading>
                <Stack spacing={4}>
                  <Box>
                    <Flex justify="space-between" mb={2}>
                      <Text fontWeight="bold">PurelyGive</Text>
                      <Text fontWeight="bold">${data.donationExample.purelyGiveNet.toFixed(2)}</Text>
                    </Flex>
                    <Box bg="green.100" h="10" borderRadius="md" width={`${data.donationExample.purelyGiveNet}%`} />
                    <Text fontSize="xs" color="gray.600" mt={1}>Nonprofit receives</Text>
                  </Box>
                  <Box>
                    <Flex justify="space-between" mb={2}>
                      <Text fontWeight="bold">{data.competitor}</Text>
                      <Text fontWeight="bold">${data.donationExample.competitorNet.toFixed(2)}</Text>
                    </Flex>
                    <Box bg="orange.100" h="10" borderRadius="md" width={`${data.donationExample.competitorNet}%`} />
                    <Text fontSize="xs" color="gray.600" mt={1}>Nonprofit receives</Text>
                  </Box>
                  <Text color="gray.700">Typical total fees may reduce a ${data.donationExample.donationAmount} donation to about ${data.donationExample.competitorNet.toFixed(2)}.</Text>
                </Stack>
              </Box>

              <Box bg="white" borderRadius="2xl" p={{ base: 4, md: 6 }} boxShadow="sm">
                <Heading size="sm" mb={2}>Estimate what platform fees may be costing your nonprofit</Heading>
                <Text color="gray.600" fontSize="sm" mb={4}>See how much your organization could retain each year.</Text>
                <Text color="gray.500" fontSize="xs" mb={4}>
                  Examples are based on publicly available pricing and typical nonprofit fee structures. Actual costs vary by contract and payment method.
                </Text>
                <Stack direction={{ base: 'column', md: 'row' }} spacing={3} mb={5} flexWrap="wrap">
                  {calculatorPresets.map((preset) => (
                    <Button
                      key={preset.label}
                      size="sm"
                      variant="outline"
                      whiteSpace="normal"
                      h="auto"
                      py={2}
                      onClick={() => {
                        setMonthlyVolume(preset.monthlyVolume);
                        setAverageGift(preset.averageGift);
                      }}
                    >
                      {preset.label} ({preset.helper})
                    </Button>
                  ))}
                </Stack>
                <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
                  <Stack>
                    <Box>
                      <Text fontSize="sm" mb={2}>Monthly donation volume ($)</Text>
                      <Input type="number" value={monthlyVolume} onChange={(e) => setMonthlyVolume(Number(e.target.value))} />
                    </Box>
                    <Box>
                      <Text fontSize="sm" mb={2}>Average gift ($)</Text>
                      <Input type="number" value={averageGift} onChange={(e) => setAverageGift(Number(e.target.value))} />
                    </Box>
                    <Text fontSize="sm" color="gray.600">Estimated number of gifts / month: {calculator.estimatedMonthlyGifts.toFixed(0)}</Text>
                  </Stack>
                  <Box bg="orange.50" borderRadius="xl" p={5}>
                    <Text fontSize="sm">Estimated annual donation volume</Text>
                    <Heading size="md" mb={4}>${calculator.totalAnnual.toLocaleString()}</Heading>
                    <Text fontSize="sm">Typical annual fees on {data.competitor}</Text>
                    <Heading size="md" mb={4}>${calculator.annualCompetitorFees.toLocaleString(undefined, { maximumFractionDigits: 0 })}</Heading>
                    <Text fontSize="sm">Estimated annual fees on PurelyGive</Text>
                    <Heading size="md" mb={4}>$0</Heading>
                    <Text fontWeight="bold">Estimated annual fees you could retain: ${calculator.annualSaved.toLocaleString(undefined, { maximumFractionDigits: 0 })}</Text>
                  </Box>
                </SimpleGrid>
              </Box>
            </SimpleGrid>
            <Text mt={2} fontSize="sm" color="gray.500">Disclaimer: {pricingDisclaimer}</Text>
          </Stack>
        </Center>
      </Box>

      <Box bg="#f5f3ee" py={10} px={{ base: 4, md: 6 }}>
        <Center>
          <Flex
            maxW="1100px"
            w="full"
            bg="orange.500"
            color="white"
            borderRadius="2xl"
            p={{ base: 6, md: 8 }}
            justify="space-between"
            align={{ base: 'flex-start', md: 'center' }}
            direction={{ base: 'column', md: 'row' }}
            gap={5}
          >
            <Box>
              <Heading size="md" mb={2}>See PurelyGive in 2 minutes</Heading>
              <Text>Quick overview of how nonprofits can receive the full donation amount through PurelyGive&rsquo;s donor-supported model.</Text>
            </Box>
            <HStack spacing={3} flexWrap="wrap" w={{ base: 'full', md: 'auto' }}>
              <Button as={Link} to="/register" bg="white" color="orange.600" _hover={{ bg: 'orange.50' }}>
                Start free
              </Button>
              <Button onClick={onVideoOpen} variant="outline" borderColor="white" color="white" _hover={{ bg: 'orange.400' }}>
                Watch 2-minute overview
              </Button>
            </HStack>
            <Text fontSize="sm" color="orange.100">
              Prefer a quick walkthrough first? <Text as={Link} to="/contact" textDecoration="underline">Contact our team.</Text>
            </Text>
          </Flex>
        </Center>
      </Box>

      <Modal isOpen={isVideoOpen} onClose={onVideoClose} isCentered size={{ base: 'full', md: '4xl' }}>
        <ModalOverlay bg="blackAlpha.700" />
        <ModalContent bg="black" color="white" borderRadius={{ base: 0, md: 'xl' }}>
          <ModalBody p={{ base: 3, md: 4 }}>
            <Flex justify="space-between" align="center" mb={3}>
              <Text fontWeight="semibold">PurelyGive 2-minute overview</Text>
              <CloseButton color="white" onClick={onVideoClose} />
            </Flex>
            <Box position="relative" pb="56.25%" h={0} borderRadius="md" overflow="hidden">
              <iframe
                title="PurelyGive two-minute overview"
                src={isVideoOpen ? overviewVideoUrl : 'about:blank'}
                style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', border: 0 }}
                allow="autoplay; encrypted-media"
                allowFullScreen
              />
            </Box>
            <Button
              as="a"
              href="https://www.youtube.com/watch?v=Xfjr5WIs-mI"
              target="_blank"
              rel="noreferrer"
              leftIcon={<ExternalLinkIcon />}
              mt={3}
              size="sm"
              variant="ghost"
              color="white"
            >
              Open on YouTube
            </Button>
          </ModalBody>
        </ModalContent>
      </Modal>

      <Box id="best-fit" style={sectionAnchorStyle} bg="#f0eee7" py={10} px={{ base: 4, md: 6 }}>
        <Center>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} maxW="1100px" w="full">
            <Box bg="white" borderRadius="2xl" p={{ base: 4, md: 6 }} boxShadow="sm">
              <Heading size="sm" mb={3}>PurelyGive is best if</Heading>
              <List spacing={2}>{data.purelyGiveBestIf.map((item) => <ListItem key={item}><ListIcon as={CheckCircleIcon} color="green.500" />{item}</ListItem>)}</List>
            </Box>
            <Box bg="white" borderRadius="2xl" p={{ base: 4, md: 6 }} boxShadow="sm">
              <Heading size="sm" mb={3}>{data.competitor} is best if</Heading>
              <List spacing={2}>{data.competitorBestIf.map((item) => <ListItem key={item}><ListIcon as={CheckCircleIcon} color="orange.500" />{item}</ListItem>)}</List>
            </Box>
          </SimpleGrid>
        </Center>
      </Box>

      <Box id="features" style={sectionAnchorStyle} bg="#f5f3ee" py={10} px={{ base: 4, md: 6 }}>
        <Center>
          <Stack maxW="1100px" w="full" spacing={4}>
            <Heading size="md">{data.featuresTitle || 'Feature highlights'}</Heading>
            {data.featuresIntro && <Text color="gray.600">{data.featuresIntro}</Text>}
            <List spacing={2}>{data.strengths.map((item) => <ListItem key={item}><ListIcon as={CheckCircleIcon} color="orange.500" />{item}</ListItem>)}</List>
          </Stack>
        </Center>
      </Box>

      {data.purelyGiveStrengths?.length > 0 && (
        <Box id="why-purelygive" style={sectionAnchorStyle} bg="#f0eee7" py={10} px={{ base: 4, md: 6 }}>
          <Center>
            <Stack maxW="1100px" w="full" spacing={4}>
              <Heading size="md">Why nonprofits choose PurelyGive</Heading>
              <List spacing={2}>
                {data.purelyGiveStrengths.map((item) => (
                  <ListItem key={item}><ListIcon as={CheckCircleIcon} color="green.500" />{item}</ListItem>
                ))}
              </List>
            </Stack>
          </Center>
        </Box>
      )}

      <Box id="faq" style={sectionAnchorStyle} bg="#f0eee7" py={10} px={{ base: 4, md: 6 }}>
        <Center>
          <Stack maxW="1100px" w="full" spacing={5}>
            <Heading size="md">Frequently asked questions</Heading>
            {data.faq.map((item) => (
              <Box key={item.question} borderBottom="1px solid" borderColor="gray.200" pb={4}>
                <Text fontWeight="bold">{item.question}</Text>
                <Text color="gray.600">{item.answer}</Text>
              </Box>
            ))}
          </Stack>
        </Center>
      </Box>

      <Box bg="#f5f3ee" py={10} px={{ base: 4, md: 6 }}>
        <Center>
          <Stack maxW="1100px" w="full" spacing={4}>
            <Heading size="sm">Compare other fundraising platforms</Heading>
            <Flex wrap="wrap" gap={3}>
              <Button as={Link} to="/" variant="ghost" w={{ base: 'full', sm: 'auto' }}>PurelyGive Home</Button>
              <Button as={Link} to="/pricing" variant="ghost" w={{ base: 'full', sm: 'auto' }}>See how PurelyGive compares</Button>
              {siblingComparisons.map((comparison) => (
                <Button key={comparison.slug} as={Link} to={`/compare/${comparison.slug}`} variant="outline" w={{ base: 'full', sm: 'auto' }}>
                  PurelyGive vs {comparison.competitor}
                </Button>
              ))}
            </Flex>
          </Stack>
        </Center>
      </Box>
    </Box>
  );
};

export default ComparisonPageTemplate;
